import NoteAddIcon from "@mui/icons-material/NoteAdd";
import SearchIcon from "@mui/icons-material/Search";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { FilePreview } from "components/FilePreview";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";
import { selectFiles } from "store/slices/files.store";
import { toFileType } from "./fileHelpers";

// The job-level Files tab shows the read-only stock-holding (system) files attached to the
// job's equipment. The request is prefetched by the job-detail page (so the data is already
// in the store when this tab is opened). Customer files live on their own CustomerFilesTab.
export const FilesTab: FC = () => {
  const intl = useIntl();
  const systemFiles = useAppSelector(selectFiles);
  const [filter, setFilter] = useState("");

  const unavailableIds = useMemo(
    () =>
      new Set(
        systemFiles.filter((f: any) => f.isUnavailable).map((f) => f.id as string)
      ),
    [systemFiles]
  );

  const files = useMemo(() => systemFiles.map(toFileType), [systemFiles]);

  const filteredFiles = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return files;
    return files.filter((f) => f?.name?.toLowerCase().includes(query));
  }, [files, filter]);

  return (
    <StyledContainer data-testid="FilesTab">
      {files.length > 0 && (
        <Grid sx={{ mb: 1 }}>
          <StyledTextField
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            variant="outlined"
            placeholder={intl.formatMessage({ id: "file.search" })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            data-testid="FilesTab-Search"
          />
        </Grid>
      )}

      {!filteredFiles.length && (
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <NoteAddIcon color="secondary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography color="secondary">
              <FormattedMessage id="file.noFilesSelected" />
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={1}>
        {filteredFiles.map(
          (f, index) =>
            f && (
              <FilePreview
                key={index}
                file={f}
                downloadOnCardClick
                readonly
                disabled={unavailableIds.has(f.id ?? "")}
              />
            )
        )}
      </Grid>
    </StyledContainer>
  );
};
